// NC1 read path (AWS-PORT chunk 4; v2 per EPOCH-KEYPAIR.md): assemble a
// verified account context from the key layer and decrypt NC1 records. The
// Virgil twin of this logic lives in E2EEHelper (E3Kit); the dispatcher there
// routes by sniffing the NC1 magic, so both schemes coexist per record
// (dual-read viewer).
//
// Port of the dev server's shared/session.js openAccountContext with ONE
// substitution: "who am I" comes from the Keystore's cached unwrapped
// identity (passkey / link / recovery unlock) instead of a password unwrap.
// Every §12 transparency check is kept:
//   - verify the hash chain BEFORE trusting any key document,
//   - owner identity key must match chain custody,
//   - member list + owner device list must match their chain commitments,
//   - provenance trust root = the OWNER's signed device list only (a
//     view-only guest holds the epoch secrets and could forge ciphertext,
//     but their devices are never valid record authors),
//   - pin the verified head so rollbacks/forks are detected across sessions.
//
// v2: the ADK rows hold each epoch's X25519 SECRET (sealed to my identity
// key); every envelope carries its own random record key sealed to the
// epoch PUBLIC key, which openParsed opens with that secret. The public key
// the member list publishes must derive from the secret I hold — a mismatch
// means the server (or a stale owner) is advertising a key nobody can open.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import store from '@/store'
import { getSodium } from './sodium'
import { loadIdentity, clearIdentity } from './Keystore'
import { Nc1ResetNotice } from './ResetNotice'
import { Nc1UnlockRequiredError, Nc1AccessNotGrantedError } from '../E2EEErrors'
import {
  b64, ub64, parseEnvelope, parseEnvelopeString, openParsed, epochPkFromSk,
  deviceListSignedBytes, memberListSignedBytes,
} from './shared/envelope.js'
import {
  verifyChain, assertDocCommitted,
  docHashIdentity, docHashDeviceList, docHashMemberList,
} from './shared/translog.js'

const NC1_MAGIC = [0x4e, 0x43, 0x31] // "NC1"

/** Does this Uint8Array carry an NC1 envelope? */
export function isNc1Bytes (bytes) {
  return bytes && bytes.length > 4 &&
    bytes[0] === NC1_MAGIC[0] && bytes[1] === NC1_MAGIC[1] && bytes[2] === NC1_MAGIC[2]
}

/** Does this base64 string decode to an NC1 envelope? (cheap prefix check) */
export function isNc1String (str) {
  if (typeof str !== 'string' || str.length < 8) return false
  // "NC1" base64-encodes to "TkMx..." — no decode needed to route.
  return str.startsWith('TkMx')
}

// Pinned transparency head, persisted per crypto accountId — the browser
// twin of the Windows keystore's TranslogHead. THE ONLY head store the
// webapp uses (the vendored translog.js defaultHeadStore is not used here).
const headStore = {
  key: (accountId) => `nc1.translogHead.${accountId}`,
  load (accountId) {
    try { return JSON.parse(localStorage.getItem(this.key(accountId))) } catch { return null }
  },
  save (accountId, head) {
    try { localStorage.setItem(this.key(accountId), JSON.stringify(head)) } catch { /* private mode */ }
  },
  forget (accountId) {
    try { localStorage.removeItem(this.key(accountId)) } catch { /* storage unavailable */ }
  },
}

/**
 * Drop the pinned head for an account whose chain was legitimately replaced
 * (finding ⑯ viewer-only re-genesis). Without this the next context build
 * trips the rollback alarm against a chain that no longer exists.
 */
export function forgetPinnedHead (accountId) {
  headStore.forget(accountId)
}

/**
 * Drop the pinned head when the identity this browser just unlocked or
 * received (Unlock.js, DeviceLink.js) signs with a key the pin never saw:
 * the pin belongs to a previous identity GENERATION — an owner reset run on
 * another surface (OWNER-RESET.md) — and the next context build would refuse
 * the new chain as a rollback with no way out (2026-09-24, josh+2: pinned
 * seq 34 under the key the reset retired, the reset chain has 10 entries;
 * every timeline decrypt failed and nothing prompted, because the identity
 * itself was fine). The evidence is the user's own: the secrets came out of
 * their passkey / recovery code / approving device, which the server cannot
 * forge. A same-generation pin (signer = this key) is kept — that is the
 * rollback detector doing its job. A rotation performed elsewhere also
 * trips this and costs one build without the checkpoint comparison; the
 * chain is still verified from genesis and re-pinned right after.
 * `adoptChainAfterRetiredPin` is the build-time twin for browsers that
 * unlocked before this existed. Returns true when a pin was dropped.
 */
export function forgetPinnedHeadIfRetired (accountId, idSignPk) {
  const pinned = headStore.load(accountId)
  if (!pinned || !idSignPk || pinned.signerPk === idSignPk) return false
  console.warn(`[nc1] pinned transparency head for ${accountId} (seq ${pinned.seq}) was signed by a key this identity does not hold - forgetting it, the identity generation changed`)
  headStore.forget(accountId)
  return true
}

// The two continuity alarms verifyChain raises against a pinned head
// (shared/translog.js is vendored, so they are matched by message, not type).
const isPinAlarm = (e) => /^transparency log (REGRESSED|FORKED)\b/.test(e && e.message)

/**
 * A pinned head tripped the rollback / fork alarm. Decide whether the pin is
 * simply from a previous identity GENERATION — the owner reset the account's
 * encryption on another surface, which replaces the chain with a new genesis
 * the old head cannot be found in — or whether this is the real thing.
 *
 * Two kinds of evidence, tried strongest first:
 *
 * 1. LOCAL, which the server cannot manufacture: this browser holds the
 *    account OWNER's identity secrets (they came out of the user's passkey /
 *    recovery code / approving device), the served chain, verified on its
 *    own merits from genesis, is signed by exactly that key, and the pin was
 *    signed by a different key. Only the holder of the secret could have
 *    produced such a chain, so it is the user's chain and the pin is retired.
 * 2. DECLARED (2026-09-24, user decision — "option C" of the partner
 *    discussion; the Windows uploader's "declared reset, not a fork" rule,
 *    OWNER-RESET.md §7.1 / §14.4): the account probe lists the signing keys
 *    every COMPLETED reset retired, and the pin was signed by one of them.
 *    This is all a PARTNER's browser can have — it holds its own identity,
 *    never the owner's, so (1) never applies to it — and all the owner's
 *    OTHER browsers have while they still hold the pre-reset identity (the
 *    stale-identity check below then clears it and asks for an unlock). It
 *    is the trade-off §4 of the reset design already accepted for devices:
 *    the reset is re-auth-gated on the server and everyone on the account
 *    is mailed. A partner is also told once, in the app, that the owner
 *    reset their encryption and that history before it is gone —
 *    dismissible, and only in a browser that actually held a stale pin
 *    (user decision; no safety-number compare step, verification stays
 *    optional as decided 2026-09-17).
 *
 * Either way the pin is forgotten and the chain adopted as a first-contact
 * browser would: it passed every signature, cross-signing and contiguity
 * check, only the checkpoint comparison is skipped, once, and the new head
 * is pinned below. Everything else re-throws the alarm unchanged: a
 * truncated chain within the SAME generation has a pinned signer no reset
 * retired (the rollback the pin exists for); a chain that does not verify
 * from genesis fails on that instead.
 * (2026-09-24 — josh+2: pinned seq 34 under the key the 09-21 reset retired,
 * a 10-entry reset chain, identity v3 already unlocked here.)
 */
async function adoptChainAfterRetiredPin (sodium, email, accountId, translog, pinned, cached, alarm) {
  const localIsOwner = !!cached && cached.altAccountId === accountId
  const idSignPk = cached && cached.secrets && cached.secrets.id_sign_pk
  const fresh = verifyChain(sodium, accountId, translog, null) // a chain that is bad on its own throws here
  if (localIsOwner && idSignPk && fresh.head.signerPk === idSignPk && pinned.signerPk !== idSignPk) {
    console.warn(`[nc1] ${alarm.message} - but the served chain is signed from genesis by the owner identity this browser holds and the pin was signed by a different key: the account's encryption was reset elsewhere; forgetting the pinned head (seq ${pinned.seq})`)
    headStore.forget(accountId)
    return fresh
  }
  // Declared reset — consulted only once an alarm fired, so the normal path
  // never pays for it. A probe that fails leaves the alarm standing.
  const probe = await E2eeApi.getAccount(email).then((r) => r.data).catch(() => null)
  const retired = Array.isArray(probe && probe.retiredIdSignPks) ? probe.retiredIdSignPks : []
  if (retired.includes(pinned.signerPk)) {
    console.warn(`[nc1] ${alarm.message} - but the pin was signed by a key a completed reset of ${email} retired (declared by the server; everyone on the account was mailed): forgetting the pinned head (seq ${pinned.seq})`)
    headStore.forget(accountId)
    if (!localIsOwner) Nc1ResetNotice.request({ ownerEmail: email, accountId, at: probe.reset && probe.reset.at })
    return fresh
  }
  throw alarm
}

// One verified context per account email, built at most once per page load
// (invalidated on unlock and on unknown-device retries so a freshly linked
// device's records become readable without a refresh).
const contextCache = new Map()

// Devices / epochs a FRESH context still did not have (see openWithRetry):
// `${email}|epoch:3` or `${email}|device:<id>` -> when that was established.
const MISS_RETRY_MS = 5 * 60 * 1000
const knownMisses = new Map()

// A context build that FAILED is not retried by every decrypt that follows
// (2026-09-24). Until then a failing build evicted the cache and each of a
// timeline's hundreds of encrypted fields rebuilt it — four e2ee reads plus
// a chain verification per field, all failing the same way (josh+2 with a
// stale pin: 160 failed fields, 640 reads, a dialog that spun for minutes).
// The failure is remembered per account for BUILD_FAILURE_RETRY_MS and handed
// back as-is, so callers see the same typed error (unlock required, access
// not granted, alarm) and the prompts still fire; a real change (unlock,
// grant, rotation) clears it through invalidateContext.
const BUILD_FAILURE_RETRY_MS = 15 * 1000
const failedBuilds = new Map() // email -> { at, error }

export function invalidateContext (email) {
  // Called when something really changed (unlock, grant, rotation, a device
  // retired), so what was missing may be there now.
  if (email) {
    const e = String(email).toLowerCase()
    contextCache.delete(e)
    failedBuilds.delete(e)
    for (const key of [...knownMisses.keys()]) if (key.startsWith(`${e}|`)) knownMisses.delete(key)
  } else {
    contextCache.clear()
    failedBuilds.clear()
    knownMisses.clear()
  }
}

/**
 * Build (or reuse) the verified account context:
 *   { sodium, devicePks: Map<deviceId,Uint8Array>, revokedDevices: Set,
 *     epochSks: Map<epoch,Uint8Array> }  (+ openParsed's lazy epochPks/recordKeys caches)
 * Throws Nc1UnlockRequiredError when this browser's keystore has no identity
 * for the account — the UI routes that to the unlock dialog.
 */
export function getContext (accountEmail) {
  const email = String(accountEmail).toLowerCase()
  if (!contextCache.has(email)) {
    const failed = failedBuilds.get(email)
    if (failed && Date.now() - failed.at < BUILD_FAILURE_RETRY_MS) return Promise.reject(failed.error)
    const p = buildContext(email)
    // A failed build must not poison the cache (e.g. transient network) —
    // but it is remembered briefly so the next hundred decrypts do not each
    // repeat it (see failedBuilds).
    p.then(() => failedBuilds.delete(email), (error) => {
      contextCache.delete(email)
      failedBuilds.set(email, { at: Date.now(), error })
    })
    contextCache.set(email, p)
  }
  return contextCache.get(email)
}

async function buildContext (email) {
  // PERF (2026-08-14): everything fetchable starts in ONE parallel wave —
  // the four API reads, the IndexedDB identity load, and the libsodium wasm
  // init. Fetch ORDER carries no trust; the §12 VERIFICATION order below is
  // unchanged and still gates everything before anything is believed. The
  // device list is prefetched by the account email — which IS the owner's
  // email by construction (accounts are keyed by their owner); the rare
  // mismatch is refetched below.
  const myEmail = String(store.state.account?.accountId || '').toLowerCase()
  const sodiumP = getSodium()
  const identityP = loadIdentity(email)
  const myIdentityP = (myEmail && myEmail !== email) ? loadIdentity(myEmail) : null
  const memberListP = E2eeApi.getMemberList(email)
  const logP = E2eeApi.getLog(email).then((r) => r.data || []).catch(() => [] /* legacy */)
  const deviceListP = E2eeApi.getDeviceList(email).then((r) => r.data).catch(() => null)
  const adkP = E2eeApi.getAdkEnvelopes(email).then((r) => r.data || []).catch(() => [])
  // If an early check throws (e.g. unlock required), the in-flight fetches
  // must not surface as unhandled rejections.
  ;[memberListP, logP, deviceListP, adkP].forEach((p) => p.catch(() => {}))

  const sodium = await sodiumP

  // Whose identity opens this account (§8.5, chunk 5 — guest-aware):
  //   1. The VIEWED account's identity cached in this browser — the owner on
  //      their own account, or a browser the owner viewer-provisioned via
  //      device-transfer (chunk 2's low-friction partner onboarding).
  //   2. Else the LOGGED-IN user's own identity — a §8.5 member/guest whose
  //      ADK rows are addressed to their own userId.
  // The context below is identity-agnostic: provenance is always verified
  // against the VIEWED account's owner, and epoch secrets are filtered by
  // whichever userId the loaded identity carries.
  let cached = await identityP
  let cachedFrom = email
  if ((!cached || !cached.secrets) && myIdentityP) {
    cached = await myIdentityP
    cachedFrom = myEmail
  }
  if (!cached || !cached.secrets) {
    // Guests unlock THEIR OWN identity (their passkey/recovery); owners
    // unlock the account's.
    throw new Nc1UnlockRequiredError(myEmail && myEmail !== email ? myEmail : email, { ownerEmail: email })
  }
  const secrets = cached.secrets
  const myUserId = cached.altAccountId

  const memberList = (await memberListP).data
  const owner = memberList.members.find((m) => m.role === 'owner')
  if (!owner) throw new Error('Account member list has no owner.')

  // §12: verify the chain before trusting any document. Legacy accounts
  // without a log fall back to TOFU on the owner key (loud warning).
  const translog = await logP
  let chain = null
  if (translog.length) {
    const pinned = headStore.load(memberList.accountId)
    try {
      chain = verifyChain(sodium, memberList.accountId, translog, pinned)
    } catch (e) {
      // A pin from a retired identity generation (owner reset run elsewhere)
      // is forgiven on the evidence this browser holds; see the helper.
      if (!pinned || !isPinAlarm(e)) throw e
      chain = await adoptChainAfterRetiredPin(sodium, email, memberList.accountId, translog, pinned, cached, e)
    }
    if (owner.idSignPk !== chain.head.signerPk) {
      throw new Error('Owner identity key does not match transparency-log custody (possible key substitution).')
    }
    if (chain.latest.identity.docHash !== docHashIdentity(sodium, {
      idEncPk: owner.idEncPk, idSignPk: owner.idSignPk, identityVersion: chain.latest.identity.docVersion,
    })) {
      throw new Error('Owner identity keys do not match the transparency-log commitment.')
    }
    assertDocCommitted('member-list', chain.latest.memberList,
      memberList.version, docHashMemberList(sodium, memberList))
  } else {
    console.warn(`[nc1] no transparency log for ${email} — legacy TOFU on the owner key`)
  }

  const ownerSigOk = sodium.crypto_sign_verify_detached(
    ub64(sodium, memberList.ownerSig), memberListSignedBytes(memberList), ub64(sodium, owner.idSignPk))
  if (!ownerSigOk) throw new Error('Account member list signature is not authentic.')

  // Stale-identity detection (post-rotation, chunk 7): if the cached identity
  // CLAIMS to be this account's owner identity but its keys no longer match
  // the (chain-verified) published owner keys, the account rotated since this
  // browser cached its copy. The stale secrets open nothing new — clear them
  // so this browser reads as locked, and route to the unlock dialog (new
  // recovery code / link from an up-to-date surface). Guests are exempt:
  // their identity is a different user's and never matches owner custody.
  if (cached.altAccountId === memberList.accountId && secrets.id_sign_pk !== owner.idSignPk) {
    await clearIdentity(cachedFrom)
    throw new Nc1UnlockRequiredError(cachedFrom)
  }

  // Provenance trust root: the OWNER's devices only.
  const devicePks = new Map()
  const revokedDevices = new Set()
  let ownerDl = await deviceListP
  if (owner.email && String(owner.email).toLowerCase() !== email) {
    // Theoretical only (account email ≠ owner email) — refetch by the
    // member list's authoritative owner email.
    try { ownerDl = (await E2eeApi.getDeviceList(owner.email)).data } catch (e) { ownerDl = null }
  }
  if (chain) {
    if (!ownerDl) throw new Error('Transparency log commits an owner device list but the server returned none.')
    assertDocCommitted('device-list', chain.latest.deviceList,
      ownerDl.version, docHashDeviceList(sodium, ownerDl))
  }
  if (ownerDl) {
    if (!sodium.crypto_sign_verify_detached(
      ub64(sodium, ownerDl.identitySig), deviceListSignedBytes(ownerDl), ub64(sodium, owner.idSignPk))) {
      throw new Error('Owner device list is not authentic under the owner identity key.')
    }
    for (const d of ownerDl.devices) {
      devicePks.set(d.deviceId, ub64(sodium, d.devSignPk))
      if (d.revokedAt != null) revokedDevices.add(d.deviceId)
    }
  }

  // My epoch secrets (one ADK row per epoch, sealed to my identity-encryption key).
  const adkEnvelopes = await adkP
  const epochSks = new Map()
  let sealedToAnotherKey = 0
  for (const env of adkEnvelopes) {
    if (env.userId !== myUserId) continue
    try {
      epochSks.set(env.keyEpoch, sodium.crypto_box_seal_open(
        ub64(sodium, env.sealedAdk), ub64(sodium, secrets.id_enc_pk), ub64(sodium, secrets.id_enc_sk)))
    } catch (e) {
      // Addressed to my userId but sealed to a key I no longer hold: the
      // owner's member-list snapshot predates my identity reset/rotation
      // (finding ⑯). For a guest that is "not granted to THIS identity yet"
      // — the owner must re-verify and re-grant — not a generic decrypt bug.
      if (cachedFrom !== email) { sealedToAnotherKey += 1; continue }
      throw e
    }
  }

  // A viewer whose OWN identity opened this context but who holds no epoch
  // secret for the viewed account has simply not been granted encrypted
  // access (invite and grant are separate steps). Say so now, rather than
  // letting every record fail and surface as a generic decrypt error telling
  // them to email support (finding ⑤c, §5.2).
  if (epochSks.size === 0 && cachedFrom !== email) {
    throw new Nc1AccessNotGrantedError(email, cachedFrom, { staleGrant: sealedToAnotherKey > 0 })
  }

  // v2 integrity: the public key the (chain-committed, owner-signed) member
  // list advertises for the CURRENT epoch must be the one my sealed secret
  // derives — otherwise uploaders are sealing to a key nobody here can open.
  const currentSk = epochSks.get(memberList.keyEpoch)
  if (currentSk && memberList.epochPk && b64(sodium, epochPkFromSk(sodium, currentSk)) !== memberList.epochPk) {
    throw new Error(`Published epoch key for epoch ${memberList.keyEpoch} does not match the sealed epoch secret (possible key substitution).`)
  }

  if (chain) headStore.save(memberList.accountId, chain.head)

  return { sodium, devicePks, revokedDevices, epochSks }
}

const isContextMiss = (error) => /unknown device|no epoch key/.test(error)

function missKey (email, parsed, error) {
  const aad = (parsed && parsed.aad) || {}
  return /no epoch key/.test(error) ? `${email}|epoch:${aad.epoch}` : `${email}|device:${aad.deviceId}`
}

/**
 * Open one parsed envelope against the account context, refreshing the
 * context when the author device is unknown or the context has no key for the
 * record's epoch (a device linked / an epoch bumped after the context was
 * cached — the NC1 twin of Virgil's stale-card retry).
 *
 * A refresh is four API reads plus a chain verification, and it only cures a
 * context that is STALE. A device or epoch that is gone for good - every
 * epoch from before a key rotation is (delete-on-rotate) - stays missing, so
 * the refresh is rationed: one rebuild, shared by every decrypt failing at
 * that moment, then the same epoch/device fails straight away for
 * MISS_RETRY_MS. (Until 2026-09-19 every failing field rebuilt the context by
 * itself; a page of screenshots from before a rotation meant hundreds of
 * rebuilds, one after another.)
 */
async function openWithRetry (email, parsed, timing) {
  const usedContext = getContext(email)
  let ctx = await usedContext
  // `timing` is DecryptTiming's dev-only stopwatch (null otherwise).
  if (timing) timing.cryptoBegin()
  let r = openParsed(ctx.sodium, parsed, ctx)
  if (timing) timing.cryptoEnd()
  let repeatMiss = false
  if (!r.ok && isContextMiss(r.error)) {
    const missedAt = knownMisses.get(missKey(email, parsed, r.error))
    if (missedAt === undefined || Date.now() - missedAt > MISS_RETRY_MS) {
      // Rebuild only if nobody has since this call read the cache; otherwise ride on theirs.
      if (contextCache.get(email) === usedContext) contextCache.delete(email)
      ctx = await getContext(email)
      if (timing) timing.cryptoBegin()
      r = openParsed(ctx.sodium, parsed, ctx)
      if (timing) timing.cryptoEnd()
      if (!r.ok && isContextMiss(r.error)) {
        const key = missKey(email, parsed, r.error)
        if (knownMisses.has(key)) {
          repeatMiss = true // another decrypt in the same wave already reported it
        } else {
          // Said ONCE per epoch/device; every later record like it carries
          // `nc1RepeatMiss` so callers need not log the same thing per record.
          console.warn(`[nc1] ${r.error} - a freshly loaded context does not have it either. `
            + 'Records sealed under an epoch that a key rotation withdrew cannot be opened by anyone. '
            + 'Further records like this fail without another refresh.')
        }
        knownMisses.set(key, Date.now())
      }
    } else {
      repeatMiss = true
    }
  }
  if (!r.ok) {
    const err = new Error(`NC1 decrypt failed: ${r.error}`)
    if (repeatMiss) err.nc1RepeatMiss = true
    throw err
  }
  if (r.revoked) console.warn('[nc1] record author device is revoked (historical record)')
  return r.plaintext
}

/** Decrypt an NC1 image blob → Blob typed image/jpeg. */
export async function decryptImageBlob (accountEmail, bytes, timing) {
  const sodium = await getSodium()
  if (timing) timing.cryptoBegin()
  const parsed = parseEnvelope(sodium, bytes)
  if (timing) timing.cryptoEnd()
  const plaintext = await openWithRetry(String(accountEmail).toLowerCase(), parsed, timing)
  return new Blob([plaintext], { type: 'image/jpeg' })
}

/** Decrypt an NC1 base64 string envelope → string. */
export async function decryptStringEnvelope (accountEmail, envelopeB64, timing) {
  const sodium = await getSodium()
  if (timing) timing.cryptoBegin()
  const parsed = parseEnvelopeString(sodium, envelopeB64)
  if (timing) timing.cryptoEnd()
  const plaintext = await openWithRetry(String(accountEmail).toLowerCase(), parsed, timing)
  return new TextDecoder().decode(plaintext)
}
